import { lineIdOf } from "../utils/variantCore";
import React, { useEffect, useState, useRef } from "react";
import { resolveInitialPayerPhone } from "../utils/deliveryPhone";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useFormik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";
import useDeliveryFee from "../redux/useDeliveryFee";
import DeliveryFeeStatus, { QuotedAmount } from "../components/DeliveryFeeStatus";
import FreightCheckoutNotice from "../components/FreightCheckoutNotice";
import {
  createBrandOrder,
  payAirtelMoneyCheckout,
  payCardCheckout,
  payMoMoCheckout,
} from "../redux/brandOrderActions";
import CreditCardForm from "../components/CreditCardForm/CreditCardForm";
import airtel from "../assets/img/airtel_money.png";
import momo from "../assets/img/momo.jpg";
import { clearCart, removeFromCart } from "../redux/brandCartSlice";
import { fetchBrandById } from "../redux/brandSlice";
import { cartOutletId, findOutlet, outletLabel } from "../utils/vendorOutlets";
import Layout from "../components/Global/Layout";
import CheckoutStepper from "../components/CheckoutStepper";
import { toast } from "react-toastify";
import {
  formatCurrency,
  isFwMomoMethod,
  fwNetworkFor,
  MTN_PREFIXES,
} from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import {
  linesSubtotal,
  effectiveMinimum,
  cartMinimumVerdict,
} from "../utils/minimumOrder";
import { extractCreatedOrderId, orderChargeAmount, ORDER_TOTAL_UNREADABLE } from "../redux/checkout";
/* The server refuses any line it cannot charge for, with 400 PRICE_ON_REQUEST
   (a quote-only line) or 400 PRICE_UNSET (a line whose price is not above zero).
   Both used to arrive here as "Payment processing failed. Please try again.",
   which is wrong twice: no payment was attempted, and retrying cannot work
   while the line is still in the cart. The shopper was left pressing a pay
   button that could never succeed. */
import {
  cartLineProblem,
  describeUnsellableLineRefusal,
  isUnsellableLineRefusal,
} from "../utils/priceOnRequest";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
} from "@mui/material";
import { followCardRedirect } from "../utils/cardPayment";
import { apiSetContactEmail } from "../api/profileApi";
import { StruckPrice, VendorOfferAmount } from "../components/PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import useCheckoutDeliverySchedule from "../hooks/useCheckoutDeliverySchedule";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import DeliveryTimePicker from "../components/DeliveryTimePicker";
import placeholderImage from "../assets/img/menuImage.png";
import {
  Heading,
  SubHeading,
  Columns,
  Panel,
  PanelBody,
  PanelTitle,
  DeliverTo,
  Pin,
  DeliverWho,
  DeliverWhere,
  PayChip,
  ItemRow,
  Thumb,
  ItemInfo,
  ItemName,
  ItemMeta,
  ItemSum,
  Summary,
  CostRow,
  Rule,
  TotalRow,
  PayLine,
  FieldRow,
  OptionalTag,
  EmailHint,
  Cta,
  AirtelCta,
  PayLogo,
  Reassure,
} from "../components/Layout/confirmLayout";
import { goToSignIn } from "../utils/signInReturn";

// Page and Wrap are this page's own; everything inside them comes from
// components/Layout/confirmLayout. Layout's MainContent already pads the page
// by the navbar's measured height (--lusuku-nav-h), so the top padding here is
// breathing room only. It used to add the navbar height a second time.
const Page = styled.div`
  background: #f4f6f6;
  min-height: 100vh;
`;

const Wrap = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  padding: 16px 20px 48px;

  @media (max-width: 760px) {
    padding: 10px 10px 24px;
  }
`;

const useStyles = makeStyles((theme) => ({
  input: {
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
}));

const validationSchema = Yup.object().shape({
  cardNumber: Yup.string().required("Card Number is required."),
  expiry_month: Yup.string().required("Expiry Month is required."),
  expiry_year: Yup.string().required("Expiry Year is required."),
  cardCvv: Yup.string().required("CVV is required."),
  cardType: Yup.string().required("Card Type is required."),
  fullName: Yup.string().required("Full Name is required."),
});

const BrandPlaceOrderScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const classes = useStyles();

  const { shippingAddress, paymentMethod, total } = useSelector(
    (state) => state.brandCart
  );
  const currentBrand = useSelector(
    (state) => state.brandCart.currentBrand || id
  );

  const cart = useSelector(
    (state) => state.brandCart.carts[currentBrand] || {}
  );

  // Filter and validate products, ensuring all required fields are present
  const products = Object.values(cart)
    .filter((item) => {
      const hasValidPrice =
        item.price !== undefined &&
        item.price !== null &&
        !isNaN(Number(item.price));
      const hasValidQuantity =
        item.quantity !== undefined &&
        item.quantity !== null &&
        !isNaN(Number(item.quantity)) &&
        Number(item.quantity) > 0;
      const hasName = item.name && item.name.trim() !== "";
      return hasValidPrice && hasValidQuantity && hasName;
    })
    .map((item) => ({
      ...item,
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 0,
      specPriceDelta: Number(item.specPriceDelta) || 0,
      // Calculate item total including options + variant deltas
      itemTotal:
        ((Number(item.price) || 0) +
          (Number(item.specPriceDelta) || 0) +
          (item.options || []).reduce(
            (sum, option) => sum + (Number(option.price) || 0),
            0
          )) *
        (Number(item.quantity) || 0),
    }));

  // What the vendor's live offers took off; the subtotal is already net of it.
  const offerSavings = cartOfferSavings(products);

  // The outlet (branch) this order is fulfilled from, read off the cart lines
  // the way the restaurant checkout reads it; null for the main store. It goes
  // to the fee quote, the delivery slots, every order line and the order body
  // (fulfilmentOutletId), so all four resolve the same address and hours.
  const outletId = cartOutletId(products);

  /* Second line of defence on the minimum order value, platform floor raised by
     the brand's own where there is one. The cart says it first and the server is
     the gate that counts; this stops a deep link or a tab left open since before
     the floor existed from posting a basket the server will only refuse after
     the customer has entered a phone number.

     `selectedBrand` is whatever brand storefront the customer came through. On a
     reload straight onto this screen it can be null, in which case the platform
     floor applies alone and the server still refuses the rest. Nothing here
     touches the quote flow: an accepted brand quote is its own order path and
     is exempt from the floor by design. */
  const selectedBrand = useSelector((state) => state.brands.selectedBrand);
  // A reload straight onto this screen leaves the brand unloaded; fetch it so
  // the outlet can be named in the summary (and the brand's own minimum
  // applies). One GET, only when the loaded brand is not this one.
  useEffect(() => {
    if (!currentBrand) return;
    if (String(selectedBrand?._id || "") === String(currentBrand)) return;
    dispatch(fetchBrandById(currentBrand));
  }, [dispatch, currentBrand, selectedBrand?._id]);
  const fulfilmentOutlet = findOutlet(selectedBrand, outletId);
  const minimumOrderValue = useMinimumOrderValue();
  const orderMinimum = effectiveMinimum(
    minimumOrderValue,
    Number(selectedBrand?.fulfilment?.minimumOrderValueUgx) || 0
  );

  /* WHAT HAPPENS WHEN THE SERVER REFUSES A LINE IT CANNOT CHARGE FOR.
     The refusal names the offending products in `violations`, so this screen can
     take exactly those lines out of the cart or send the shopper to the product
     to request a quote, instead of a toast that tells them a payment failed and
     leaves them with no move. The dialog carries the SERVER'S OWN message: it is
     written against the state of the catalogue at the moment of the POST, which
     a checkout tab left open for an hour no longer knows. */
  const [priceRefusal, setPriceRefusal] = useState(null);

  /**
   * True when this failure was an unchargeable line, in which case the dialog is
   * now open and the caller must not toast anything of its own.
   *
   * Takes either a rejected thunk action (the body is `action.payload`) or a
   * raw error, because the four payment paths below hold one or the other.
   */
  const noteUnsellableLineRefusal = (source) => {
    const body =
      source && typeof source === "object" && "payload" in source
        ? source.payload
        : source;
    if (!isUnsellableLineRefusal(body)) return false;
    setPriceRefusal(describeUnsellableLineRefusal(body));
    return true;
  };

  /* The cart lines this screen would take out. Read from the cart rather than
     from `products`, because `products` already filters out a line with no
     price at all and those are exactly the lines at issue. */
  const unsellableCartLines = Object.values(cart).filter((item) =>
    Boolean(cartLineProblem(item))
  );

  const removeRefusedLines = () => {
    /* Prefer the ids the server named. When it named nobody (an older server, or
       a refusal that carried only a message), fall back to every line this
       client can see is unchargeable, which is the same rule the cart uses. */
    const namedIds = priceRefusal?.productIds?.length
      ? priceRefusal.productIds
      : unsellableCartLines.map((item) => item._id).filter(Boolean);
    namedIds.forEach((productId) => {
      dispatch(removeFromCart({ brandId: currentBrand, productId }));
    });
    setPriceRefusal(null);
    if (namedIds.length) {
      toast.info(
        namedIds.length === 1
          ? "Item removed. You can request a quote for it from the shop."
          : "Items removed. You can request a quote for them from the shop.",
      );
    }
    history.push("/cart");
  };

  const goToRefusedProduct = () => {
    const productId = priceRefusal?.productIds?.[0];
    setPriceRefusal(null);
    history.push(
      productId
        ? `/brand/shop/${currentBrand}/product/${productId}`
        : `/brand/shop/${currentBrand}`,
    );
  };

  /** Normalised line items for POST /mobile (brand storefront checkout). */
  const buildOrderItems = () =>
    products.map((item) => ({
      name: item.name || "Unknown Product",
      vendorId: item.brandId || currentBrand,
      // The outlet this line is fulfilled from (the server validates it
      // belongs to the brand). Omitted for the main store.
      ...(outletId ? { outletId } : {}),
      qty: Number(item.quantity) || 0,
      price: Number(item.price) || 0,
      image: item.image || "",
      productId: item._id,
      lineId: lineIdOf(item),
      options: item.options || [],
      // Per-line dynamic specs forwarded straight to Order.orderItems.
      // selectedSpecs are the buyer's variant picks; specPriceDelta is
      // the per-unit adjustment they triggered; productSpecsSnapshot
      // captures the vendor-fixed attributes at order time so the
      // dashboard order view stays accurate even if the catalogue
      // is later edited.
      selectedSpecs: item.selectedSpecs || {},
      specPriceDelta: Number(item.specPriceDelta) || 0,
      productSpecsSnapshot: item.specs || {},
    }));

  /**
   * Persist brand order semantics on the Order document (dashboard, 3PL, reporting).
   * Branded-packaging flow keeps server-side AWAITING_ARTWORK handling.
   */
  const brandOrderMeta = () => {
    const needsArtwork = products.some(
      (item) =>
        item.effectiveOrderFlow === "BRANDED_PACKAGING" ||
        item.orderFlowOverride === "BRANDED_PACKAGING"
    );
    // The outlet the whole order is fulfilled from, named once at the top of
    // the body as well as on every line. Spread into every payment path's
    // order body below, so no path can leave it out.
    const fulfilment = outletId ? { fulfilmentOutletId: outletId } : {};
    if (needsArtwork) {
      return {
        isBrandOrder: true,
        brandId: currentBrand,
        brandFlowType: "BRANDED_PACKAGING",
        ...fulfilment,
      };
    }
    return {
      isBrandOrder: true,
      brandId: currentBrand,
      brandFlowType: "STANDARD",
      brandFlowStage: "NONE",
      ...fulfilment,
    };
  };

  const user = useSelector((state) => state.user.currentUser);

  const { orderCreationStatus, paymentCreationStatus } = useSelector(
    (state) => state.brandOrder
  );

  const [orderSource] = useState("Web");
  const [postData, setPostData] = useState({
    mobile: "",
  });  
  // Payer prefill: most customers pay with the number we deliver to (or their
  // account number), so seed the mobile-money field once instead of forcing a
  // retype. Fully editable for "someone else is paying", and the once-guard
  // means we never fight a customer who clears the field on purpose.
  const payerPrefilledRef = useRef(false);
  useEffect(() => {
    if (payerPrefilledRef.current) return;
    const prefill = resolveInitialPayerPhone(shippingAddress, user);
    if (prefill) {
      payerPrefilledRef.current = true;
      setPostData((d) => (d.mobile ? d : { ...d, mobile: prefill }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress, user]);


  // The active market's MTN prefixes (market.js momoPrefixes), 10 local
  // digits. This was a hard-coded Uganda regex (077/076/078/039) — a Zambian
  // MTN number starts 096, so on lusuku.co.zm the Pay button could never
  // enable. With no prefix table for a market, a plain 10-digit check applies.
  const MTN_REGEX =
    MTN_PREFIXES.length > 0
      ? new RegExp(`^(${MTN_PREFIXES.join("|")})\\d{7}$`)
      : /^0\d{9}$/;
  const MTN_PREFIX_LIST =
    MTN_PREFIXES.length > 1
      ? `${MTN_PREFIXES.slice(0, -1).join(", ")}, or ${MTN_PREFIXES[MTN_PREFIXES.length - 1]}`
      : MTN_PREFIXES[0] || "0";
  const isMtnValid = MTN_REGEX.test(postData.mobile);
  const mtnTouched = postData.mobile.length > 0;

  const userId = user?.result?._id || user?._id;
  const userName = user?.result?.name || user?.result?.businessName || user?.name || user?.businessName;
  const userEmail = user?.result?.email || user?.email;
  // Optional email capture — phone-only customers have no address on file.
  // Never required: it must not gate any pay button.
  const [emailInput, setEmailInput] = useState("");
  const needsEmail = !userEmail;

  useEffect(() => {
    if (!userId) {
      goToSignIn(history);
    }
  }, [userId, history]);

  useEffect(() => {
    if (
      Object.keys(shippingAddress).length === 0 ||
      !paymentMethod ||
      products.length === 0
    ) {
      history.push(`/brand/shop/${currentBrand}`);
    }
  }, [shippingAddress, paymentMethod, products, history, currentBrand]);

  // Server-authoritative delivery fee (distance-based, signed quote). Resets any
  // stale fee from a previous order before fetching fresh (matches prior behaviour).
  const {
    fee: serverShippingFee,
    offer: deliveryOffer,
    distanceKm,
    quoteToken,
    isReady: deliveryFeeReady,
    isFailed: deliveryFeeFailed,
    isBlocked: deliveryFeeBlocked,
    error: deliveryFeeError,
    refresh: refreshDeliveryFee,
    itemsSubtotal: quotedItemsSubtotal,
    total: quotedTotal,
    minimumOrder: quotedMinimumOrder,
    goodsPriced: quotedGoodsPriced,
    paymentProcessing,
    fulfillment: quoteFulfillment,
    freightConsentRequired,
  } = useDeliveryFee({
    vendorId: products?.[0]?.brandId || currentBrand,
    // An outlet has its own coordinates and radius; /api/quote measures from
    // the outlet when told which one, and order-create prices from the same
    // outlet (it reads the id off the lines), so the fee shown is the fee
    // charged.
    outletId,
    customerLat: shippingAddress?.latLng?.lat,
    customerLng: shippingAddress?.latLng?.lng,
    resetOnVendorChange: true,
    items: products,
    // The checkout's payment method: the server's total then carries the
    // processing fee order create adds for it.
    paymentMethod,
  });
  /* Second line of defence on the minimum order value: the SERVER's verdict
     for the quoted lines (minimumOrder on POST /api/quote), the cart's own
     sum only until that answer arrives. The server is the gate at order create. */
  const belowMinimumOrder =
    cartMinimumVerdict(
      { goodsPriced: quotedGoodsPriced, itemsSubtotal: quotedItemsSubtotal, minimumOrder: quotedMinimumOrder },
      { subtotal: linesSubtotal(products), minimum: orderMinimum }
    ).shortfall > 0;

  // Express or a scheduled window from the published hours of the brand, or
  // of the outlet when the order is fulfilled from one (the same ids the fee
  // is quoted for).
  const deliveryTime = useCheckoutDeliverySchedule({
    flow: "brand",
    vendorId: products?.[0]?.brandId || currentBrand,
    outletId,
  });

  /* The cart check at its branch (4 Oct 2026). On this page's load the cart
     is checked (a cart read back from localStorage, or moved, may hold a line
     the branch no longer sells at that price), and every create-order call
     below goes through guardSubmit: checked first when a line is
     unconfirmed, and a 409 BRANCH_LINES_CHANGED opens "Changes at <branch>"
     from the server's own lines. A failed check never blocks the order. */
  const branchCheck = useCartBranchCheck({
    vertical: "brand",
    cartId: currentBrand,
    checkWhen: Boolean(currentBrand),
  });
  const submitOrder = (thunk, payload) =>
    branchCheck.guardSubmit(() => deliveryTime.submitOrder(thunk, payload));
  // Sent as the order body's totalPrice, which order create recomputes. The
  // screen shows the server's total (quotedTotal), and every rail charges the
  // created order's own totalPrice (orderChargeAmount below).
  const totalOrderCostPlusShipping = Number(total) + Number(serverShippingFee);

  useEffect(() => {
    if (paymentMethod === "MOMO" && !isFwMomoMethod("MOMO") && orderCreationStatus === "fulfilled") {
      // Direct-MTN status page is Uganda-only; FW-momo markets redirect to Flutterwave.
      history.replace("/momoOrderPage");
    } else if (paymentMethod === "COD" && orderCreationStatus === "fulfilled") {
      history.replace("/orderPage");
    }
  }, [orderCreationStatus, paymentMethod, history]);

  const formik = useFormik({
    initialValues: {
      cardNumber: "",
      expiry_month: "",
      expiry_year: "",
      cardCvv: "",
      cardType: "",
      fullName: "",
    },
    validationSchema,
    onSubmit: (values) => {
      cardPlaceOrderHandler(values);
    },
  });

  const placeOrderHandler = (e) => {
    e.preventDefault();

    // Validate products before creating order
    if (products.length === 0) {
      toast.error("No valid products in cart. Please add items to your cart.");
      return;
    }

    const orderItems = buildOrderItems();

    // Recalculate totals to ensure accuracy. Includes specPriceDelta
    // so buyer-configurable variant pricing flows into itemsPrice.
    const calculatedItemsPrice = products.reduce((sum, item) => {
      const itemPrice = Number(item.price) || 0;
      const itemQuantity = Number(item.quantity) || 0;
      const specDelta = Number(item.specPriceDelta) || 0;
      const optionsTotal = (item.options || []).reduce(
        (optSum, option) => optSum + (Number(option.price) || 0),
        0
      );
      return sum + (itemPrice + specDelta + optionsTotal) * itemQuantity;
    }, 0);

    const calculatedTotal = calculatedItemsPrice + Number(serverShippingFee);

    submitOrder(createBrandOrder, {
      orderItems,
      shippingAddress,
      // Server recomputes the fee/total; quote token lets it honour the
      // displayed fee. No client-computed fee is sent as authoritative.
      clientQuoteToken: quoteToken,
      distanceKm,
      paymentMethod,
      itemsPrice: calculatedItemsPrice,
      totalPrice: calculatedTotal,
      customerName:
        userName || "Customer",
      client_id: userId,
      orderSrc: orderSource,
      vendorId: currentBrand,
      qty: products.map((item) => Number(item.quantity) || 0),
      image: products.map((item) => item.image || ""),
      ...brandOrderMeta(),
    }).then((action) => {
      // Only clear the cart once the order is persisted: a refused order, or a
      // stop to pick a delivery time, keeps the cart for the retry.
      if (!action.error && action.meta?.requestStatus === "fulfilled") {
        dispatch(clearCart(currentBrand));
        return;
      }
      noteUnsellableLineRefusal(action);
    });
  };

  const payMoMoCheckoutHandler = async (e) => {
    e.preventDefault();

    // MARKET RAIL SWITCH: ZM "MOMO" charges over the FW rail (network MTN).
    if (isFwMomoMethod("MOMO")) {
      return payAirtelMoneyCheckoutHandler(e, fwNetworkFor("MOMO"));
    }

    // WHY save but not send: no MoMo charge payload in this codebase carries an
    // email, so we only persist it to the profile — that is where the order
    // confirmation is addressed from. Best-effort; it must never block the pay.
    const typedEmail = emailInput.trim();
    if (!userEmail && typedEmail) {
      apiSetContactEmail(typedEmail).catch(() => {});
    }

    const orderItems = buildOrderItems();

    // Create + await the order BEFORE charging. Previously payment and
    // createBrandOrder fired concurrently/un-awaited, so a customer could be
    // charged with no order (or one the coverage gate later rejects).
    const orderAction = await submitOrder(createBrandOrder, {
      orderItems,
      shippingAddress,
      // Server recomputes the fee/total; quote token lets it honour the
      // displayed fee. No client-computed fee is sent as authoritative.
      clientQuoteToken: quoteToken,
      distanceKm,
      paymentMethod,
      itemsPrice: total,
      totalPrice: totalOrderCostPlusShipping,
      customerName: user?.result?.name || "Customer",
      client_id: userId,
      orderSrc: orderSource,
      vendorId: currentBrand,
      ...brandOrderMeta(),
    });

    // Stop the flow if the order failed (rejected / 4xx / 422). The thunk
    // already surfaced the backend message (e.g. OUT_OF_DELIVERY_RANGE), except
    // for an unchargeable line, which opens the dialog above instead.
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      noteUnsellableLineRefusal(orderAction);
      return;
    }

    const orderId = extractCreatedOrderId(orderAction);
    // THE amount this order is charged: its server totalPrice, as order
    // create returned it (redux/checkout orderChargeAmount), never a sum
    // made on this screen.
    const chargeAmount = orderChargeAmount(orderAction);
    if (chargeAmount === null) {
      toast.error(ORDER_TOTAL_UNREADABLE, { autoClose: false });
      return;
    }

    dispatch(
      payMoMoCheckout({
        mobile: postData.mobile,
        totalPrice: chargeAmount,
        client_id: userId,
        orderItems,
        // Bind payment to the order we just created (backend ignores until updated).
        orderId,
      })
    );
  };

  const generateTxRef = (clientId) => {
    return clientId + "-" + Date.now();
  };

  const payAirtelMoneyCheckoutHandler = async (e, networkOverride) => {
    e.preventDefault();

    if (products.length === 0) {
      toast.error("No valid products in cart. Please add items to your cart.");
      return;
    }

    const orderItems = buildOrderItems();

    // Create + await the order before charging (see payMoMoCheckoutHandler).
    const orderAction = await submitOrder(createBrandOrder, {
      orderItems,
      shippingAddress,
      // Server recomputes the fee/total; quote token lets it honour the
      // displayed fee. No client-computed fee is sent as authoritative.
      clientQuoteToken: quoteToken,
      distanceKm,
      paymentMethod,
      itemsPrice: total,
      totalPrice: totalOrderCostPlusShipping,
      customerName: user?.result?.name || "Customer",
      client_id: userId,
      orderSrc: orderSource,
      vendorId: currentBrand,
      ...brandOrderMeta(),
    });

    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      noteUnsellableLineRefusal(orderAction);
      return;
    }

    const orderId = extractCreatedOrderId(orderAction);
    // THE amount this order is charged: its server totalPrice, as order
    // create returned it (redux/checkout orderChargeAmount), never a sum
    // made on this screen.
    const chargeAmount = orderChargeAmount(orderAction);
    if (chargeAmount === null) {
      toast.error(ORDER_TOTAL_UNREADABLE, { autoClose: false });
      return;
    }
    // Embed the order id in tx_ref as `${clientId}-${orderId}` so the
    // Flutterwave webhook can bind payment to THIS order.
    const uniqueTxRef = orderId ? `${userId}-${orderId}` : generateTxRef(userId);

    // Use whatever they typed for THIS charge and save it to the profile too.
    // Best-effort: a failed save must never stop the payment.
    const typed = emailInput.trim();
    const effectiveEmail = userEmail || typed || undefined;
    if (!userEmail && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }

    dispatch(
      payAirtelMoneyCheckout({
        mobile: postData.mobile,
        totalPrice: chargeAmount,
        client_id: userId,
        email: effectiveEmail,
        uniqueTxRef,
        network: networkOverride || fwNetworkFor(paymentMethod) || paymentMethod,
        redirect_url: `${window.location.origin}/airtelPay`,
        orderItems,
        orderId,
      })
    ).then((action) => {
      if (!action.error && action.payload?.data?.meta?.authorization) {
        followCardRedirect(action.payload.redirectUrl);
      } else if (!action.error) {
        // The order already EXISTS: createOrder ran and was awaited above, so
        // silence here leaves the customer holding an unpaid order with no next
        // step and no idea anything went wrong. A REJECTED thunk has already
        // toasted the backend's own reason, so re-toasting it would just double
        // up; a 200 whose body carries no `meta.authorization` had no branch at
        // all, and that is the case this else was missing.
        toast.error(
          "We couldn't start the Airtel Money payment. Your order was created but has NOT been paid \u2014 open it in My Orders to try again.",
          { autoClose: false }
        );
      }
    });
  };

  const cardPlaceOrderHandler = () => {
    if (products.length === 0) {
      toast.error("No valid products in cart. Please add items to your cart.");
      return;
    }

    // Same optional-email treatment as Airtel (see payAirtelMoneyCheckoutHandler).
    const typed = emailInput.trim();
    const effectiveEmail = userEmail || typed || undefined;
    if (!userEmail && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }

    const cardDetails = {
      cardNumber: formik.values.cardNumber,
      expiry_month: formik.values.expiry_month,
      expiry_year: formik.values.expiry_year,
      cardCvv: formik.values.cardCvv,
      cardType: formik.values.cardType,
      fullName: formik.values.fullName,
    };

    // Recalculate totals (includes specPriceDelta from buyer-
    // configurable variants).
    const calculatedItemsPrice = products.reduce((sum, item) => {
      const itemPrice = Number(item.price) || 0;
      const itemQuantity = Number(item.quantity) || 0;
      const specDelta = Number(item.specPriceDelta) || 0;
      const optionsTotal = (item.options || []).reduce(
        (optSum, option) => optSum + (Number(option.price) || 0),
        0
      );
      return sum + (itemPrice + specDelta + optionsTotal) * itemQuantity;
    }, 0);

    const calculatedTotal = calculatedItemsPrice + Number(serverShippingFee);

    const orderItems = buildOrderItems();

    // Create + await the order before charging the card (see MoMo handler).
    return submitOrder(createBrandOrder, {
      orderItems,
      shippingAddress,
      // Server recomputes the fee/total; quote token lets it honour the
      // displayed fee. No client-computed fee is sent as authoritative.
      clientQuoteToken: quoteToken,
      distanceKm,
      paymentMethod,
      itemsPrice: calculatedItemsPrice,
      totalPrice: calculatedTotal,
      customerName: user?.result?.name || "Customer",
      client_id: userId,
      orderSrc: orderSource,
      vendorId: currentBrand,
      ...brandOrderMeta(),
    }).then((orderAction) => {
      if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
        noteUnsellableLineRefusal(orderAction);
        return;
      }

      const orderId = extractCreatedOrderId(orderAction);
      // THE amount this order is charged: its server totalPrice, as order
      // create returned it (redux/checkout orderChargeAmount), never a sum
      // made on this screen.
      const chargeAmount = orderChargeAmount(orderAction);
      if (chargeAmount === null) {
        toast.error(ORDER_TOTAL_UNREADABLE, { autoClose: false });
        return;
      }

      return dispatch(
        payCardCheckout({
          cardDetails,
          totalPrice: chargeAmount,
          client_id: userId,
          customer_name: userName || "Customer",
          email: effectiveEmail,
          network: paymentMethod,
          redirect_url: `${window.location.origin}/cardOrder`,
          orderId,
        })
      )
        .then((action) => {
          if (action.payload?.redirectUrl) {
            followCardRedirect(action.payload.redirectUrl);
          }
        })
        .catch((err) => {
          console.error("Error while dispatching payCardCheckout:", err);
          /* A line the server will not charge for is not a payment failure, and
             the old blanket message sent the shopper back to the card form to
             try again at something that cannot succeed. */
          if (noteUnsellableLineRefusal(err)) return;
          toast.error("Payment processing failed. Please try again.");
        });
    });
  };

  // Rendered inside each payment block below. Deliberately not `required`, not
  // validated, and absent from every disabled= expression: ask, never block.
  const optionalEmailField = needsEmail ? (
    <FieldRow>
      <TextField
        className={classes.input}
        label={
          <span>
            Email <OptionalTag>optional</OptionalTag>
          </span>
        }
        placeholder="you@example.com"
        variant="outlined"
        size="small"
        type="email"
        fullWidth
        name="contactEmail"
        autoComplete="email"
        value={emailInput}
        onChange={(ev) => setEmailInput(ev.target.value)}
      />
      <EmailHint>
        Add an email and we'll send your receipt and order updates there.
      </EmailHint>
    </FieldRow>
  ) : null;

  let airtelButtonText;
  switch (true) {
    case paymentCreationStatus === "processing payment":
      airtelButtonText = "Processing Payment...";
      break;
    case paymentCreationStatus === "fulfilled":
      airtelButtonText = "Payment Initiated!";
      break;
    case paymentCreationStatus === "rejected":
      airtelButtonText = "Payment attempt Failed, try again!";
      break;
    case orderCreationStatus === "pending":
      airtelButtonText = "Creating Order...";
      break;
    case orderCreationStatus === "fulfilled":
      airtelButtonText = "Order Created...";
      break;
    default:
      airtelButtonText = "Proceed to Pay";
  }

  // Presentation only.
  const deliverName = shippingAddress?.locationName?.name || "Not set";
  const deliverWhere = [
    shippingAddress?.locationName?.vicinity,
    shippingAddress?.customerNumber,
  ]
    .filter(Boolean)
    .join(" · ");
  const paymentDisplay =
    {
      COD: "Cash on delivery",
      MOMO: "MTN Mobile Money",
      Airtel: "Airtel Money",
      ZAMTEL: "Zamtel Money",
      VISA: "Card",
    }[paymentMethod] ||
    paymentMethod ||
    "Not set";
  const itemCount = products.reduce((n, p) => n + (Number(p.quantity) || 0), 0);
  const feeStatusProps = {
    isReady: deliveryFeeReady,
    isFailed: deliveryFeeFailed,
    isBlocked: deliveryFeeBlocked,
    error: deliveryFeeError,
    onRetry: refreshDeliveryFee,
  };
  // Every pay button waits on the same things; each adds its own on top.
  const cannotPay =
    orderCreationStatus === "pending" ||
    products.length === 0 ||
    !deliveryFeeReady ||
    freightConsentRequired ||
    belowMinimumOrder ||
    /* Second line of defence, the same shape as belowMinimumOrder above. The
       cart already refuses to send anyone here with an unchargeable line, but a
       deep link or a tab left open since before that check existed can still
       land on this screen, and the only thing a pay button could do then is
       collect a phone number and fail. */
    unsellableCartLines.length > 0;
  const paying =
    orderCreationStatus === "pending" || paymentCreationStatus === "pending";
  const spinner = (
    <CircularProgress size={18} style={{ marginRight: 8, color: "#ffffff" }} />
  );

  return (
    <Layout>
      <BranchCartReviewDialog {...branchCheck.dialogProps} />
      <Page>
        <Wrap>
          <CheckoutStepper currentStep="confirm" compact />
          <FreightCheckoutNotice />

          <Heading>Review &amp; confirm</Heading>
          <SubHeading>One last look before we place your order.</SubHeading>

          <Columns>
            {/* LEFT: where and what */}
            <div>
              <Panel>
                <DeliverTo>
                  <Pin>
                    <LocationOnIcon style={{ fontSize: 18 }} />
                  </Pin>
                  <div style={{ minWidth: 0 }}>
                    <DeliverWho>Deliver to · {deliverName}</DeliverWho>
                    {deliverWhere && <DeliverWhere>{deliverWhere}</DeliverWhere>}
                  </div>
                  <PayChip>{paymentDisplay}</PayChip>
                </DeliverTo>
                <PanelBody>
                  <PanelTitle>
                    Your order · {itemCount} item{itemCount !== 1 ? "s" : ""}
                  </PanelTitle>
                  {unsellableCartLines.length > 0 && (
                    <ItemMeta style={{ color: "#b45309", fontWeight: 600 }}>
                      {unsellableCartLines.length === 1
                        ? "One item in your cart has to be quoted by the vendor before it can be ordered. Open your cart to remove it or request a quote."
                        : `${unsellableCartLines.length} items in your cart have to be quoted by the vendor before they can be ordered. Open your cart to remove them or request a quote.`}
                    </ItemMeta>
                  )}
                  {products.length === 0 ? (
                    <ItemMeta>No items in cart. Please add items to proceed.</ItemMeta>
                  ) : (
                    products.map((item) => {
                      const optionsTotal = (item.options || []).reduce(
                        (sum, option) => sum + (Number(option.price) || 0),
                        0
                      );
                      const unitPrice =
                        item.price + item.specPriceDelta + optionsTotal;
                      const optionNames = (item.options || [])
                        .map((opt) => opt.name || "")
                        .filter(Boolean)
                        .join(", ");
                      return (
                        <ItemRow key={item._id || item.name}>
                          <Thumb
                            src={item.image || placeholderImage}
                            alt={item.name}
                            onError={(e) => {
                              e.target.src = placeholderImage;
                            }}
                          />
                          <ItemInfo>
                            <ItemName>{item.name}</ItemName>
                            <ItemMeta>
                              Qty {item.quantity} ·{" "}
                              {lineHasDeal(item) && (
                                <StruckPrice>
                                  {formatCurrency(
                                    (Number(item.listPrice) || 0) +
                                      item.specPriceDelta +
                                      optionsTotal
                                  )}
                                </StruckPrice>
                              )}
                              {formatCurrency(unitPrice)}
                              {optionNames && ` · ${optionNames}`}
                            </ItemMeta>
                          </ItemInfo>
                          <ItemSum>{formatCurrency(item.itemTotal)}</ItemSum>
                        </ItemRow>
                      );
                    })
                  )}
                </PanelBody>
              </Panel>

              <DeliveryTimePicker controller={deliveryTime} />

              {/* Card details live in the wide column; the pay button stays in
                  the summary so the money reads top to bottom. */}
              {paymentMethod === "VISA" && (
                <Panel style={{ marginTop: 16 }}>
                  <PanelBody>
                    <PanelTitle>Card details</PanelTitle>
                    <CreditCardForm
                      onFormChange={(data) => {
                        /* The component takes `onFormChange` and nothing else.
                           This mount passed `formik`, so the handler it calls on
                           mount was undefined and card payment on this page threw
                           the moment it rendered. */
                        let expiry_month = "";
                        let expiry_year = "";
                        if (typeof data.expirationDate === "string") {
                          [expiry_month, expiry_year] =
                            data.expirationDate.split("/");
                        }
                        formik.setValues({
                          cardNumber: data.cardNumber,
                          expiry_month: expiry_month || "",
                          expiry_year: expiry_year || "",
                          cardCvv: data.cvv,
                          cardType: data.cardType,
                          fullName: data.fullName,
                        });
                      }}
                    />
                  </PanelBody>
                </Panel>
              )}
            </div>

            {/* RIGHT: the money, sticky; the Total is the loudest element */}
            <Summary>
              <Panel>
                <PanelBody>
                  <CostRow>
                    <span className="k">Subtotal</span>
                    <span className="v"><QuotedAmount {...feeStatusProps} amount={quotedItemsSubtotal} /></span>
                  </CostRow>
                  {offerSavings > 0 && (
                    <CostRow>
                      <span className="k">Vendor offer</span>
                      <VendorOfferAmount className="v">
                        -{formatCurrency(offerSavings)}
                      </VendorOfferAmount>
                    </CostRow>
                  )}
                  <CostRow>
                    <span className="k">Delivery</span>
                    <span className="v" style={{ color: "#03660c", fontWeight: 600 }}>
                      <DeliveryFeeStatus
                        {...feeStatusProps}
                        freightQuotedSeparately={quoteFulfillment?.required === true}
                        offer={deliveryOffer}
                        value={formatCurrency(Number(serverShippingFee) || 0)}
                      />
                    </span>
                  </CostRow>
                  {outletId && (
                    <CostRow>
                      <span className="k">Fulfilled from</span>
                      <span
                        className="v"
                        title={fulfilmentOutlet ? outletLabel(fulfilmentOutlet) : undefined}
                      >
                        {fulfilmentOutlet?.name || "Selected outlet"}
                      </span>
                    </CostRow>
                  )}
                  <Rule />
                  <TotalRow>
                    <span className="k">Total</span>
                    <span className="v">
                      <QuotedAmount
                        {...feeStatusProps}
                        amount={quotedTotal}
                        paymentProcessing={paymentProcessing}
                        failedMessage="We couldn't work out the total."
                      />
                    </span>
                  </TotalRow>
                  <PayLine>
                    Paying by <b>{paymentDisplay}</b>
                  </PayLine>

                  {paymentMethod === "COD" && (
                    <>
                      <Cta onClick={placeOrderHandler} disabled={cannotPay}>
                        {orderCreationStatus === "pending" ? (
                          <>
                            {spinner}
                            Creating order...
                          </>
                        ) : (
                          "Place order"
                        )}
                      </Cta>
                      <Reassure>
                        You won't be charged now. You pay the rider on delivery.
                      </Reassure>
                    </>
                  )}

                  {paymentMethod === "MOMO" && (
                    <>
                      <FieldRow>
                        <TextField
                          className={classes.input}
                          label="MTN mobile number"
                          placeholder={`e.g. ${MTN_PREFIXES[0] || "07"}1234567`}
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={postData.mobile}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                            setPostData({ ...postData, mobile: val });
                          }}
                          error={mtnTouched && !isMtnValid}
                          helperText={
                            mtnTouched && !isMtnValid
                              ? `Enter a valid MTN number (${MTN_PREFIX_LIST})`
                              : isMtnValid
                              ? "Valid MTN number"
                              : `MTN numbers start with ${MTN_PREFIX_LIST}`
                          }
                          FormHelperTextProps={{
                            style: { color: isMtnValid ? "#16a34a" : undefined },
                          }}
                          inputProps={{ inputMode: "numeric", maxLength: 10 }}
                        />
                      </FieldRow>
                      {optionalEmailField}
                      <Cta
                        onClick={payMoMoCheckoutHandler}
                        disabled={
                          cannotPay ||
                          paymentCreationStatus === "pending" ||
                          !isMtnValid
                        }
                      >
                        {paying ? (
                          <>
                            {spinner}
                            Processing...
                          </>
                        ) : (
                          <>
                            <PayLogo src={momo} alt="" />
                            Pay with MTN Mobile Money
                          </>
                        )}
                      </Cta>
                      <Reassure>
                        You'll get a prompt on this number to approve the payment.
                      </Reassure>
                    </>
                  )}

                  {(paymentMethod === "Airtel" || paymentMethod === "ZAMTEL") && (
                    <>
                      <FieldRow>
                        <TextField
                          className={classes.input}
                          label="Mobile number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={postData.mobile}
                          onChange={(e) =>
                            setPostData({ ...postData, mobile: e.target.value })
                          }
                        />
                      </FieldRow>
                      {optionalEmailField}
                      <AirtelCta
                        onClick={payAirtelMoneyCheckoutHandler}
                        disabled={
                          cannotPay ||
                          paymentCreationStatus === "processing payment" ||
                          !postData.mobile
                        }
                      >
                        <PayLogo src={airtel} alt="" />
                        {airtelButtonText}
                      </AirtelCta>
                      <Reassure>
                        You'll get a prompt on this number to approve the payment.
                      </Reassure>
                    </>
                  )}

                  {paymentMethod === "VISA" && (
                    <>
                      {optionalEmailField}
                      <Cta
                        onClick={formik.handleSubmit}
                        disabled={
                          cannotPay ||
                          paymentCreationStatus === "pending" ||
                          !formik.isValid
                        }
                      >
                        {paying ? (
                          <>
                            {spinner}
                            Processing...
                          </>
                        ) : (
                          "Pay with card"
                        )}
                      </Cta>
                      <Reassure>
                        You'll be redirected to complete the card payment securely.
                      </Reassure>
                    </>
                  )}
                </PanelBody>
              </Panel>
            </Summary>
          </Columns>
        </Wrap>

        {/* The refusal, with the two moves that actually resolve it: take the
            named lines out, or go to the product and ask for the quote. Closing
            it leaves the checkout exactly as it was, which is why the pay
            buttons stay disabled while such a line is in the cart. */}
        <Dialog
          open={Boolean(priceRefusal)}
          onClose={() => setPriceRefusal(null)}
          aria-labelledby="price-refusal-title"
          aria-describedby="price-refusal-description"
        >
          <DialogTitle id="price-refusal-title">
            {priceRefusal?.title || "This item needs a quote"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="price-refusal-description">
              {priceRefusal?.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={() => setPriceRefusal(null)} color="inherit">
              Close
            </MuiButton>
            <MuiButton onClick={goToRefusedProduct} color="primary">
              Request a quote
            </MuiButton>
            <MuiButton
              onClick={removeRefusedLines}
              color="error"
              variant="contained"
            >
              {(priceRefusal?.count || 1) > 1
                ? "Remove these items"
                : "Remove this item"}
            </MuiButton>
          </DialogActions>
        </Dialog>
      </Page>
    </Layout>
  );
};

export default BrandPlaceOrderScreen;
